import { AccessSpace } from '@upright/common/iam/access/types';
import { CompanyType } from '@upright/common/model/input/companies/types';
import { createCompany } from '@upright/ui/api/model/input/companies';
import { Button } from '@upright/ui/common/generic/components/button';
import { Input } from '@upright/ui/common/generic/components/input';
import { Modal } from '@upright/ui/common/generic/components/modal/Modal';
import { Heading, Text } from '@upright/ui/common/generic/components/typography';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateCompanyModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const trimmedName = name.trim();
  const canSubmit = !isSubmitting && trimmedName.length > 0;

  const handleClose = () => {
    setName('');
    setError(undefined);
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    const newCompany = await createCompany({
      accessSpace: AccessSpace.SHARED,
      type: CompanyType.COMPANY,
      name: trimmedName,
    });
    if (!newCompany) {
      setIsSubmitting(false);
      setError('Unable to create new company');
      return;
    }
    handleClose();
    navigate(`/company/${newCompany.id}/${newCompany.name}/internal/basic`);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} aria-label="Create a company">
      <Content>
        <Heading level={2} size="xl">
          Create a company
        </Heading>
        <Form
          onSubmit={event => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <Input
            label="Company name"
            value={name}
            onChange={setName}
            placeholder="Enter company name"
            isRequired
            autoFocus
            isDisabled={isSubmitting}
          />
          {error ? (
            <Text size="sm" color="red-500">
              {error}
            </Text>
          ) : null}
          <Actions>
            <Button variant="outlined" type="button" onPress={handleClose}>
              Cancel
            </Button>
            <Button type="submit" isDisabled={!canSubmit} isPending={isSubmitting}>
              Create
            </Button>
          </Actions>
        </Form>
      </Content>
    </Modal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xl);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space__sm);
`;
